import { ArrowLeft, ArrowRight, RotateCcw, SkipForward, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AccessCollectIcon } from '@/components/icons/AccessCollectIcon';
import { Button } from '@/components/ui/button';
import { useOnboarding } from '@/contexts/onboarding-context';
import { cn } from '@/lib/utils';
import { OnboardingCompletion } from './onboarding-completion';
import { OnboardingStepIndicator } from './onboarding-step-indicator';
import { OnboardingWelcome } from './onboarding-welcome';
import { Step1CompanySetup } from './steps/step1-company-setup';
import { Step2UserAccess } from './steps/step2-user-access';
import { Step3GeneralSettings } from './steps/step3-general-settings';
import { Step4Templates } from './steps/step4-templates';
import { Step5CollectionStrategies } from './steps/step5-collection-strategies';
import { Step6DisputeTypes } from './steps/step6-dispute-types';

// step 0 = welcome, 1-6 = wizard steps, 7 = completion
const WELCOME = 0;
const COMPLETION = 7;

interface OnboardingWizardProps {
  userName: string;
  initialCompanyCount?: number;
  initialStep?: number;
}

export function OnboardingWizard({ userName, initialCompanyCount = 0, initialStep = WELCOME }: OnboardingWizardProps) {
  const { completeOnboarding, dismissOnboarding, resetOnboarding, saveStep } = useOnboarding();

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [companiesAdded, setCompaniesAdded] = useState(initialCompanyCount);
  const [userAccessSkipped, setUserAccessSkipped] = useState(false);
  const [generalSettingsModified, setGeneralSettingsModified] = useState(false);

  useEffect(() => {
    saveStep(currentStep);
  }, [currentStep, saveStep]);

  const goToStep = (step: number) => setCurrentStep(step);
  const next = () => setCurrentStep((s) => s + 1);
  const back = () => setCurrentStep((s) => s - 1);

  const handleSkipAll = () => dismissOnboarding();
  const handleReset = () => {
    setCurrentStep(WELCOME);
    setCompaniesAdded(initialCompanyCount);
    setUserAccessSkipped(false);
    setGeneralSettingsModified(false);
    resetOnboarding();
  };

  const isInStep = currentStep >= 1 && currentStep <= 6;

  return (
    <div className="fixed inset-0 z-50 flex bg-background">
      {/* ── Left sidebar ── */}
      <aside className="flex w-72 shrink-0 flex-col border-r bg-card">
        {/* Branding */}
        <div className="flex h-16 items-center gap-2 border-b px-6">
          <AccessCollectIcon className="h-12 w-12" />
          <span className="text-xl font-bold">Access Collect</span>
        </div>

        {/* Setup label */}
        <div className="px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Onboarding Setup</p>
        </div>

        {/* Step indicator */}
        <div className="flex-1 overflow-y-auto pb-6">
          <OnboardingStepIndicator currentStep={currentStep} />
        </div>

        {/* Dev: reset button at bottom of sidebar */}
        <div className="border-t p-3">
          <button
            onClick={handleReset}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Resume Onboarding
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b px-8">
          <div>
            {currentStep === WELCOME && <p className="text-sm text-muted-foreground">Welcome to Access Collect</p>}
            {isInStep && <p className="text-sm text-muted-foreground">Step {currentStep} of 6</p>}
            {currentStep === COMPLETION && <p className="text-sm text-muted-foreground">Setup complete</p>}
          </div>

          {/* Skip onboarding — testing shortcut */}
          {currentStep !== COMPLETION && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkipAll}
              className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Skip Onboarding
            </Button>
          )}
        </div>

        {/* Step content area */}
        <div
          className={cn(
            'flex-1 min-h-0 flex flex-col',
            currentStep === WELCOME || currentStep === COMPLETION
              ? 'items-center justify-center overflow-y-auto'
              : 'p-8',
          )}
        >
          {currentStep === WELCOME && (
            <OnboardingWelcome userName={userName} onStart={() => goToStep(1)} onSkip={handleSkipAll} />
          )}

          {currentStep === 1 && (
            <Step1CompanySetup companiesAdded={companiesAdded} onCompanyAdded={() => setCompaniesAdded((n) => n + 1)} />
          )}

          {currentStep === 2 && <Step2UserAccess skipped={userAccessSkipped} />}

          {currentStep === 3 && <Step3GeneralSettings />}
          {currentStep === 4 && <Step4Templates />}
          {currentStep === 5 && <Step5CollectionStrategies />}
          {currentStep === 6 && <Step6DisputeTypes />}

          {currentStep === COMPLETION && (
            <OnboardingCompletion
              companiesAdded={companiesAdded}
              userAccessSkipped={userAccessSkipped}
              generalSettingsModified={generalSettingsModified}
              onFinish={completeOnboarding}
            />
          )}
        </div>

        {/* ── Fixed bottom action bar (steps 1–6 only) ── */}
        {isInStep && (
          <div className="shrink-0 flex items-center justify-between border-t bg-background px-8 py-4">
            {/* Left: Back + Skip */}
            <div className="flex items-center gap-2">
              {currentStep > 1 && (
                <Button variant="outline" onClick={back} className="gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              {currentStep > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (currentStep === 2) setUserAccessSkipped(true);
                    if (currentStep === 3) setGeneralSettingsModified(false);
                    next();
                  }}
                  className="gap-2 text-muted-foreground"
                >
                  <SkipForward className="h-4 w-4" /> Skip for now
                </Button>
              )}
            </div>

            {/* Right: status text + Next/Finish */}
            <div className="flex items-center gap-4">
              {currentStep === 1 && (
                <p className="text-xs text-muted-foreground">
                  {companiesAdded === 0
                    ? 'You must add at least one company to continue.'
                    : `${companiesAdded} ${companiesAdded === 1 ? 'company' : 'companies'} added — you can add more or continue.`}
                </p>
              )}
              <Button
                onClick={() => {
                  if (currentStep === 3) setGeneralSettingsModified(true);
                  next();
                }}
                disabled={currentStep === 1 && companiesAdded === 0}
                className="gap-2"
              >
                {currentStep === 6 ? 'Finish Setup' : 'Next Step'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
